import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import {
  acumularProducto,
  esRuido,
  normalizarClave,
  parsearFechaCaducidad,
} from "../LeerPdf/comun";
import { border, headerStyle, textStyle, numeroStyle } from "../ExcelEstilos";

/**
 * Lectura y plantilla del Excel de lotes de Recibir Insumos (OC, Factura sin OC, Remisión).
 *
 * Sin React ni antd a propósito, para poder probarlo con un node desechable. Produce el
 * mismo Map que los parsers de LeerPdf, así que el empate contra el form lo hace
 * aplicarLotesAlForm igual que con un PDF.
 *
 * Acepta dos orígenes: la plantilla que genera construirPlantilla, y el Excel propio del
 * proveedor, reconociendo sus encabezados por sinónimos.
 */

// Sinónimos de cada columna, ya normalizados (minúsculas, sin acentos). Se compara contra
// el encabezado sin signos de puntuación, así "No. Lote" llega como "no lote".
const SINONIMOS = {
  clave: [
    "clave", "codigo", "cod", "sku", "clave proveedor", "codigo proveedor",
    "no identificacion", "noidentificacion", "numero de identificacion",
    "codigo de barras", "codigo barras", "ean", "upc", "clave producto",
  ],
  descripcion: ["descripcion", "producto", "articulo", "concepto", "nombre", "descripcion del producto"],
  lote: ["lote", "no lote", "num lote", "numero de lote", "numero lote", "lote no", "batch", "lot"],
  caducidad: [
    "caducidad", "fecha caducidad", "fecha de caducidad", "cad", "vencimiento",
    "fecha vencimiento", "fecha de vencimiento", "expiracion", "fecha expiracion",
    "fecha de expiracion", "exp", "expiry", "expiration",
  ],
  cantidad: ["cantidad", "cant", "piezas", "pzas", "unidades", "qty", "cantidad lote", "cantidad a recibir"],
};

// Los Excel de proveedor suelen traer membrete (razón social, folio, fecha) antes de la tabla
const FILAS_MAX_BUSQUEDA_ENCABEZADO = 15;

export const COLUMNAS_PLANTILLA = ["Clave", "Descripción", "Pendiente", "Lote", "Caducidad", "Cantidad"];

const normalizarEncabezado = (valor) =>
  normalizarClave(valor).replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

const esVacio = (valor) => valor === undefined || valor === null || String(valor).trim() === "";

/**
 * Dice qué índice de columna corresponde a cada campo, o null si el renglón no parece
 * un encabezado. Hace falta (clave o descripción) y (lote o cantidad).
 */
export function detectarColumnas(encabezados) {
  const columnas = {};
  (encabezados || []).forEach((celda, indice) => {
    const texto = normalizarEncabezado(celda);
    if (!texto) return;
    for (const [campo, sinonimos] of Object.entries(SINONIMOS)) {
      if (columnas[campo] !== undefined) continue;
      if (sinonimos.includes(texto)) {
        columnas[campo] = indice;
        return;
      }
    }
  });
  const tieneProducto = columnas.clave !== undefined || columnas.descripcion !== undefined;
  const tieneDato = columnas.lote !== undefined || columnas.cantidad !== undefined;
  return tieneProducto && tieneDato ? columnas : null;
}

/**
 * Caducidad de una celda: serial de Excel (celda con formato fecha, el archivo se lee sin
 * cellDates), Date, o texto en cualquiera de los formatos que ya entiende LeerPdf
 * (31/07/2027, 2028-03-01, 30-Apr-2028, 31-JUL-27...). Devuelve moment o null.
 */
export function parsearCaducidad(valor) {
  if (esVacio(valor)) return null;
  if (valor instanceof Date) {
    return Number.isNaN(valor.getTime())
      ? null
      : moment({ year: valor.getFullYear(), month: valor.getMonth(), day: valor.getDate() });
  }
  if (typeof valor === "number") {
    if (!Number.isFinite(valor) || valor <= 0) return null;
    const partes = XLSX.SSF.parse_date_code(valor);
    if (!partes) return null;
    const fecha = moment({ year: partes.y, month: partes.m - 1, day: partes.d });
    return fecha.isValid() ? fecha : null;
  }
  return parsearFechaCaducidad(String(valor).trim());
}

const parsearCantidad = (valor) => {
  if (esVacio(valor)) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : NaN;
  const numero = Number(String(valor).replace(/[$,\s]/g, ""));
  return Number.isFinite(numero) ? numero : NaN;
};

// El lote se guarda como texto: un lote numérico ("0012") no debe perder sus ceros, pero si
// Excel ya lo convirtió en número, al menos que no salga como 12.0000001
const textoLote = (valor) => (typeof valor === "number" ? String(valor) : String(valor ?? "").trim());

/**
 * @param {Array<Array<any>>} filas  hoja leída con sheet_to_json(hoja, { header: 1 })
 * @returns {{ mapa: Map|null, errores: string[], renglones: number }}
 *   mapa null = no se encontró un renglón de encabezados reconocible.
 *   Cada entrada del mapa trae además `etiqueta` (clave/descr. tal cual venía) para
 *   reportar lo que no empató.
 */
export function parsearHojaLotes(filas) {
  const errores = [];
  const lista = Array.isArray(filas) ? filas : [];

  let filaEncabezado = -1;
  let columnas = null;
  for (let i = 0; i < Math.min(lista.length, FILAS_MAX_BUSQUEDA_ENCABEZADO); i++) {
    columnas = detectarColumnas(lista[i]);
    if (columnas) {
      filaEncabezado = i;
      break;
    }
  }
  if (!columnas) return { mapa: null, errores, renglones: 0 };

  const mapa = new Map();
  let renglones = 0;
  const celda = (fila, campo) => (columnas[campo] === undefined ? null : fila[columnas[campo]]);

  for (let i = filaEncabezado + 1; i < lista.length; i++) {
    const fila = lista[i] || [];
    const numeroFila = i + 1; // como lo ve el usuario en Excel
    const clave = esVacio(celda(fila, "clave")) ? "" : String(celda(fila, "clave")).trim();
    const descripcion = esVacio(celda(fila, "descripcion")) ? "" : String(celda(fila, "descripcion")).trim();
    const loteCrudo = celda(fila, "lote");
    const cantidadCruda = celda(fila, "cantidad");
    const caducidadCruda = celda(fila, "caducidad");

    // Renglones de la plantilla que el usuario no llenó (sólo traen clave/descripción/
    // pendiente) y renglones de totales o en blanco del Excel del proveedor
    if (esVacio(loteCrudo) && esVacio(cantidadCruda)) continue;

    if (!clave && !descripcion) {
      errores.push(`Fila ${numeroFila}: no trae clave ni descripción del producto.`);
      continue;
    }
    // Renglón de totales/subtotales del Excel del proveedor, no es un producto
    if (!clave && esRuido(descripcion)) continue;
    const etiqueta = [clave, descripcion].filter(Boolean).join(" - ");

    const cantidad = parsearCantidad(cantidadCruda);
    if (Number.isNaN(cantidad) || (cantidad !== null && cantidad < 0)) {
      errores.push(`Fila ${numeroFila} (${etiqueta}): la cantidad "${cantidadCruda}" no es válida.`);
      continue;
    }

    const lote = esVacio(loteCrudo) ? "" : textoLote(loteCrudo);
    let caducidad = null;
    if (!esVacio(caducidadCruda)) {
      caducidad = parsearCaducidad(caducidadCruda);
      if (!caducidad) {
        // Medio dato prellenado le ahorra tecleo al usuario: se carga el lote sin caducidad
        errores.push(`Fila ${numeroFila} (${etiqueta}): no se entendió la caducidad "${caducidadCruda}", se cargó el lote sin ella.`);
      }
    }

    // La entrada se agrupa por clave cuando la hay (dos productos distintos pueden compartir
    // descripción) y la descripción queda como alias hacia la misma entrada: acumularProducto
    // agrupa por su campo `descripcion` y registra `codigo` como alias.
    const entrada = acumularProducto(mapa, {
      descripcion: clave || descripcion,
      codigo: clave ? descripcion : "",
      lote,
      caducidad,
      cantidad: cantidad === null ? null : cantidad,
    });
    if (entrada && !entrada.etiqueta) entrada.etiqueta = etiqueta;
    renglones++;
  }

  return { mapa, errores, renglones };
}

/** Etiquetas de las entradas del mapa que no empataron con ningún renglón del form. */
export function entradasSinEmpatar(mapa, entradasUsadas) {
  const vistas = new Set();
  const resultado = [];
  for (const entrada of mapa.values()) {
    if (vistas.has(entrada)) continue; // varias claves apuntan a la misma entrada
    vistas.add(entrada);
    if (!entradasUsadas.has(entrada)) resultado.push(entrada.etiqueta);
  }
  return resultado;
}

// ─── Plantilla ───────────────────────────────────────────────────────────────

// Las columnas que captura el usuario van en azul claro para distinguirlas de las informativas
const estiloCaptura = { ...textStyle, fill: { fgColor: { rgb: "E6F7FF" } } };
// Formato texto: una caducidad tecleada como 31/07/2027 se queda como texto y no depende de
// la configuración regional de Excel (dd/mm vs mm/dd)
const estiloCapturaTexto = { ...estiloCaptura, numFmt: "@" };
const estiloInformativo = { ...textStyle, fill: { fgColor: { rgb: "F2F2F2" } } };
const estiloTitulo = {
  font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "007A8A" } },
  alignment: { horizontal: "left", vertical: "center" },
  border,
};

const celda = (valor, estilo) =>
  typeof valor === "number"
    ? { v: valor, t: "n", s: estilo }
    : { v: valor === undefined || valor === null ? "" : valor, t: "s", s: estilo };

/**
 * Renglones de la plantilla a partir del Form.List `insumos`: uno por renglón pendiente.
 * Los que manejan lote van primero; los demás también se incluyen, porque el Excel puede
 * llenar sólo la cantidad a recibir.
 */
export function renglonesPlantilla(insumosForm, todasInsumos) {
  const renglones = [];
  (insumosForm || []).forEach((renglon) => {
    const info = (todasInsumos || []).find((x) => x._id === renglon?.insumoId);
    if (!info) return;
    const cantidad = Number(renglon.cantidad) || 0;
    const recibido = Number(renglon.recibido) || 0;
    const pendiente = Math.round((cantidad - recibido) * 1e6) / 1e6;
    if (cantidad > 0 && pendiente <= 0) return; // ya recibido completo
    renglones.push({
      clave: info.codigo || info.clave || info.codigoBarras || "",
      descripcion: info.descripcion || info.descripcionInsumo || "",
      pendiente: cantidad > 0 ? pendiente : null,
      conLotes: !!info.lotesCaducidades,
    });
  });
  return renglones.sort((a, b) => Number(b.conLotes) - Number(a.conLotes));
}

/** Libro de xlsx-js-style listo para escribir. */
export function construirPlantilla(insumosForm, todasInsumos, titulo) {
  const renglones = renglonesPlantilla(insumosForm, todasInsumos);
  const aoa = [
    [celda(titulo || "Carga de lotes", estiloTitulo), ...COLUMNAS_PLANTILLA.slice(1).map(() => celda("", estiloTitulo))],
    COLUMNAS_PLANTILLA.map((c) => celda(c, headerStyle)),
    ...renglones.map((r) => [
      celda(r.clave, estiloInformativo),
      celda(r.descripcion, estiloInformativo),
      r.pendiente === null ? celda("", { ...numeroStyle, fill: estiloInformativo.fill }) : celda(r.pendiente, { ...numeroStyle, fill: estiloInformativo.fill }),
      celda("", estiloCapturaTexto),
      celda("", estiloCapturaTexto),
      celda("", { ...estiloCaptura, alignment: { horizontal: "right" } }),
    ]),
  ];

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = [{ wch: 18 }, { wch: 50 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 12 }];
  hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNAS_PLANTILLA.length - 1 } }];

  const instrucciones = [
    "Cómo llenar la plantilla de lotes",
    "",
    "• Un renglón por lote. Si un insumo llega en varios lotes, copia su renglón y cambia Lote, Caducidad y Cantidad.",
    "• Lote: número de lote tal como viene en la caja o en la factura del proveedor.",
    "• Caducidad: texto DD/MM/AAAA (por ejemplo 31/07/2027). También se aceptan 2027-07-31 y 31-JUL-27.",
    "• Cantidad: piezas de ese lote. La cantidad a recibir del insumo será la suma de sus lotes.",
    "• Para un insumo sin lote basta con llenar Cantidad.",
    "• Los renglones sin Lote ni Cantidad se ignoran. No cambies Clave ni Descripción: con ellas se empata cada renglón.",
    "• También puedes subir el Excel del proveedor si trae columnas Código/Clave o Descripción, Lote, Caducidad y Cantidad.",
  ];
  const hojaInstrucciones = XLSXStyle.utils.aoa_to_sheet(
    instrucciones.map((linea, i) => [celda(linea, i === 0 ? estiloTitulo : { alignment: { wrapText: true, vertical: "top" } })])
  );
  hojaInstrucciones["!cols"] = [{ wch: 110 }];

  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Lotes");
  XLSXStyle.utils.book_append_sheet(libro, hojaInstrucciones, "Instrucciones");
  return { libro, renglones: renglones.length };
}

/** Primera hoja de un archivo como matriz de celdas crudas (fechas como serial). */
export function leerFilasDeArchivo(buffer) {
  const libro = XLSX.read(buffer, { type: "array" });
  // La plantilla trae "Lotes" primero; un Excel de proveedor, lo que traiga en la primera hoja
  const nombre = libro.SheetNames.includes("Lotes") ? "Lotes" : libro.SheetNames[0];
  const hoja = libro.Sheets[nombre];
  if (!hoja) return [];
  return XLSX.utils.sheet_to_json(hoja, { header: 1, defval: null, blankrows: false });
}
